import { forwardRef } from 'react';
import { Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { safeHref } from '@/lib/safeUrl';

type Variant = 'primary' | 'outline' | 'accent-solid' | 'accent-soft';
type Size = 'sm' | 'md';

const KNOWN_EXTENSIONS = new Set([
  'pdf', 'jpg', 'jpeg', 'png', 'webp', 'avif', 'gif', 'svg',
  'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp',
  'txt', 'rtf', 'zip', 'rar', '7z', 'mp4', 'mp3', 'mov', 'webm',
]);

function getExtension(url: string | null | undefined): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  const match = trimmed.match(/\.([a-zA-Z0-9]+)(?:\?.*)?(?:#.*)?$/);
  return match ? match[1].toLowerCase() : null;
}

function isBlankUrl(url: string | null | undefined): boolean {
  return !url || !url.trim();
}

function isKnownType(fileType: string | undefined, url: string | null | undefined): boolean {
  if (fileType && fileType.trim()) return true;
  const ext = getExtension(url);
  if (!ext) return false;
  return KNOWN_EXTENSIONS.has(ext);
}

export interface DownloadButtonProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'download'> {
  /** File URL. Passed through safeHref. */
  href: string | null | undefined;
  /** Visible label (button text). */
  label: string;
  /** Human file description used in aria-label, e.g. "Regulamin DL 2026". */
  fileTitle?: string;
  /** File type shown in aria-label, e.g. "PDF" | "JPG". */
  fileType?: string;
  /** Value for the download attribute (suggested filename). */
  fileName?: string;
  /** MIME type hint. */
  mimeType?: string;
  variant?: Variant;
  size?: Size;
  /** Optional icon override; default is a Download glyph. */
  icon?: React.ReactNode;
  /** Force same-tab navigation (default false — opens in new tab). */
  sameTab?: boolean;
}

const VARIANT_CLASSES: Record<Variant, string> = {
  primary:
    'bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-primary focus-visible:ring-offset-background',
  outline:
    'border border-primary text-primary hover:bg-primary/10 focus-visible:ring-primary focus-visible:ring-offset-background',
  'accent-solid':
    'bg-accent text-accent-foreground hover:bg-accent/90 focus-visible:ring-accent focus-visible:ring-offset-secondary',
  'accent-soft':
    'bg-secondary-foreground/10 hover:bg-secondary-foreground/20 focus-visible:ring-accent focus-visible:ring-offset-secondary',
};

const SIZE_CLASSES: Record<Size, string> = {
  sm: 'text-sm px-4 py-2',
  md: 'text-sm px-5 py-2.5',
};

/**
 * Unified download / external-file link.
 * Guarantees consistent aria-label, visible focus ring, and safe rel attributes
 * when opening in a new tab.
 *
 * The button is disabled when the record has a blank URL or an unknown file type.
 * In that state it renders as an non-interactive link with a clear aria-label
 * explaining why the file is unavailable.
 */
const DownloadButton = forwardRef<HTMLAnchorElement, DownloadButtonProps>(function DownloadButton(
  {
    href,
    label,
    fileTitle,
    fileType,
    fileName,
    mimeType,
    variant = 'primary',
    size = 'sm',
    icon,
    sameTab = false,
    className,
    'aria-label': ariaLabelProp,
    children,
    ...rest
  },
  ref,
) {
  const blankUrl = isBlankUrl(href);
  const knownType = isKnownType(fileType, href);
  const disabled = blankUrl || !knownType;

  const inNewTab = !sameTab && !disabled;

  const descriptor = [fileTitle, fileType && `w formacie ${fileType}`]
    .filter(Boolean)
    .join(' ');
  const suffix = inNewTab ? ' (otwiera się w nowej karcie)' : '';
  const enabledAria =
    ariaLabelProp ??
    (descriptor ? `Pobierz: ${descriptor}${suffix}` : `${label}${suffix}`);

  const disabledReason = blankUrl ? 'brak adresu do pobrania' : 'nieznany format pliku';
  const disabledAria =
    ariaLabelProp ??
    `Plik niedostępny${fileTitle ? `: ${fileTitle}` : ''} — ${disabledReason}`;

  const computedAria = disabled ? disabledAria : enabledAria;

  const safe = safeHref(href, '#');

  return (
    <a
      ref={ref}
      href={disabled ? '#' : safe}
      aria-disabled={disabled ? 'true' : undefined}
      tabIndex={disabled ? -1 : undefined}
      onClick={disabled ? (e) => e.preventDefault() : undefined}
      {...(inNewTab
        ? { target: '_blank', rel: 'noopener noreferrer external' }
        : {})}
      {...(!disabled && fileName ? { download: fileName } : {})}
      {...(!disabled && mimeType ? { type: mimeType } : {})}
      aria-label={computedAria}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        disabled && 'opacity-60 cursor-not-allowed pointer-events-none',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className,
      )}
      {...rest}
    >
      {children ?? (
        <>
          {icon ?? <Download className="w-4 h-4" aria-hidden="true" />}
          <span>{label}</span>
          {inNewTab && <span className="sr-only"> (nowa karta)</span>}
        </>
      )}
    </a>
  );
});

export default DownloadButton;
export { DownloadButton };
